// FilterBar — the filters ABOVE the table, in one compact line.
//
// Operator, 18 Sept 2026, with a screenshot of the Trades screen: "how can we
// make this left side console look better?" — the left rail was a 236px card
// holding Search and then five chip groups (When 7 · Type 4 · Asset 10 ·
// Status 4) wrapping over twelve ragged lines, beside a table that wanted the
// whole width. So the rail is gone: the filters sit in one flex-wrap row at
// the top of the table card, each one a labelled group rendered INLINE —
// a small uppercase word, then its control on the same line — and the table
// takes the full width beneath. Below lg the same row wraps into a stack;
// there is no second layout.
//
// WHICH CONTROL A FILTER GETS (decided once, for every screen):
//   the search box            .input, w-56
//   the period                PeriodChips — the most-used control stays chips
//   a short list (≤ 4 words)  chips (FilterRail's Chip: Type All/Buy/Sell/MC)
//   a long list               a <select> whose first option is "All" — one
//                             control, one line, no wrapping (Asset, Status,
//                             Money's What)
//   a name (Party, Account)   Combobox, w-48
//   Show reversed             a chip at the row's end
//   Clear filters             a btn-link at the row's end, when anything is set
//
// The toolbar line ("N deals · today · vol …" / "N entries", Export CSV,
// "Reading…") sits on ITS OWN LINE under the bar — `toolbar` — the same seat
// on the Trades log and on Money movements, so the count never moves as the
// bar wraps. FilterGroup / Chip stay in FilterRail.tsx for the screens still
// stacking their filters in a column (Trading).
//
// Usage (inside the table card, above the scroller):
//   <FilterBar
//     toolbar={<><p …>12 deals · today</p><button …>Export CSV</button></>}
//     trailing={<>
//       <Chip active={undone} onClick={…}>Show reversed</Chip>
//       {filtered && <button className="btn-link text-xs" onClick={clearAll}>Clear filters</button>}
//     </>}
//   >
//     <FilterItem label="Search"><input className="input text-xs w-56" … /></FilterItem>
//     <FilterItem label="When"><PeriodChips value={period} onChange={setPeriod} allTime /></FilterItem>
//     <FilterItem label="Type">{KINDS.map(…<Chip …/>)}</FilterItem>
//     <FilterItem label="Asset">
//       <FilterSelect value={asset} onChange={setAsset} allValue="all" options={assets.map((a) => ({ value: a, label: a }))} />
//     </FilterItem>
//   </FilterBar>

import { useEffect, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

/** The row of filters at the top of a table card, and the toolbar line
 *  beneath it. `children` are FilterItems (and any chip that stands alone);
 *  `trailing` sits at the row's end — Show reversed, Clear filters;
 *  `toolbar` is the count line with its buttons, on its own line under the
 *  row, in the seat the table card's toolbar has always had. */
export function FilterBar({ children, more, moreOpen = false, trailing, toolbar, className = '' }: {
  children: ReactNode;
  /** The filters behind "More filters" ON THE PHONE (operator, 20 Sept
   *  2026: the bar was a 300px block of six labelled controls above the
   *  first entry); at lg+ they stand in the row like any other. The fold
   *  opens by itself while one of them is set (`moreOpen`), so a filter
   *  never works unseen. */
  more?: ReactNode;
  moreOpen?: boolean;
  trailing?: ReactNode;
  toolbar?: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(moreOpen);
  useEffect(() => { if (moreOpen) setOpen(true); }, [moreOpen]);
  const folded = more !== undefined && more !== null && more !== false;
  return (
    <div className={`lg:shrink-0 ${className}`}>
      {/* THE PHONE STACKS, lg WRAPS (operator, 19 Sept 2026: "fix the phone
          layout for the filter bar"). Below lg each item is a full-width
          line — the search box across, the period chips a strip that scrolls
          sideways, the dropdowns side by side (FilterPair) — and the trailing
          chip/link a last line; at lg+ the same items wrap into one row. */}
      <div className="flex flex-col gap-2 px-3 py-2 border-b border-tg-secondary lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-4 lg:gap-y-2 lg:border-stroke-soft lg:px-3.5">
        {children}
        {folded && (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden self-start inline-flex items-center gap-1 text-meta hover:text-tg-text ${!open && moreOpen ? 'text-tg-accent' : 'text-tg-hint'}`}
          >
            {/* Closed over a filter that is set, the word says so — a
                filter never works unseen. */}
            {open ? 'Fewer filters' : moreOpen ? 'More filters · in use' : 'More filters'}
            <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        )}
        {folded && <div className={`${open ? 'flex flex-col gap-2' : 'hidden'} lg:contents`}>{more}</div>}
        {/* Never folded: Clear filters must be one tap away while a search
            or a period outside the fold is set. */}
        {trailing && <div className="flex flex-wrap items-center gap-2 lg:ml-auto">{trailing}</div>}
      </div>
      {toolbar && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-tg-secondary lg:border-stroke-soft lg:px-3.5">
          {toolbar}
        </div>
      )}
    </div>
  );
}

/** One labelled filter, inline: the word, then its control on the same
 *  line. The chips inside keep their own gap (FilterRail's 1.5 / 1). */
export function FilterItem({ label, children, className = '' }: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-2 min-w-0 ${className}`}>
      {/* ONE WIDTH FOR THE WORD on the phone, so every control starts on
          one vertical line (operator, 20 Sept 2026: "organising data in the
          same vertical line"); at lg+ the word is as wide as it is. */}
      <span className="w-14 lg:w-auto text-label uppercase tracking-[0.06em] text-tg-hint font-medium whitespace-nowrap shrink-0">
        {label}
      </span>
      {/* flex-1 below lg: the control fills the line beside its word. */}
      <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1 lg:flex-none lg:gap-1">{children}</div>
    </div>
  );
}

/** Two or three items SIDE BY SIDE on the phone — the dropdowns, the two
 *  pickers — each taking an equal share of the line; at lg+ the wrapper
 *  dissolves (contents) and the items join the row like any other. */
export function FilterPair({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`flex gap-3 min-w-0 [&>*]:flex-1 [&>*]:min-w-0 lg:contents lg:[&>*]:flex-none ${className}`}>{children}</div>;
}

/** The long list's control: one <select>, its first option "All". The
 *  screen keeps its own value for "All" (`allValue` — '' on Money, 'all' on
 *  the Trades log), so the query it builds does not change. */
export function FilterSelect<V extends string>({ value, onChange, options, allValue, allLabel = 'All', className = '' }: {
  value: V;
  onChange: (next: V) => void;
  options: ReadonlyArray<{ value: V; label: string }>;
  /** The value that means "don't filter" — the first option carries it. */
  allValue: V;
  allLabel?: string;
  className?: string;
}) {
  return (
    <select
      className={`select w-full lg:w-auto lg:text-meta ${className}`}
      value={value}
      onChange={(e) => onChange(e.target.value as V)}
    >
      <option value={allValue}>{allLabel}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}
